import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfigProvider, InputNumber, Select, Switch, TimePicker } from "antd";

import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";
import { useOnboarding } from "../../../../contexts/OnboardingContext";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import {
    buildTimeZoneSelectOptions,
    operationalHoursToPickerValue,
    pickerValueToOperationalHours,
    resolveCompanyTimeZone,
} from "../../../../helpers/operationalHours";
import { BUFFER_TIME_OPTIONS } from "../../../settings/components/preferences/BufferTimeSelector";

const { RangePicker } = TimePicker;

const DEFAULT_BUFFER_MINUTES = 60;
const DEFAULT_MIN_AGE = 18;

/**
 * Onboarding step for the rules the booking site enforces.
 *
 * This step existed in the backend (`onboardingSteps.stepStatus.preferences`) and in the URL
 * redirect table long before it existed as a page — visiting `?step=preferences` silently
 * bounced you to categories, so the flag could never be set. Operators only discovered
 * operational hours later, in Settings, if at all.
 */
const Preferences = forwardRef((props, ref) => {
    const { t } = useTranslation("onboarding");
    const { updateStepProgress, STEP_KEY_TO_INDEX } = useOnboarding();
    const { myCompanyData, fetchCompanyData } = useUser();
    const toast = useToast();

    const [enableOperationalHours, setEnableOperationalHours] = useState(false);
    const [operationalHours, setOperationalHours] = useState(null);
    const [timezone, setTimezone] = useState(null);
    const [bufferTimeMinutes, setBufferTimeMinutes] = useState(DEFAULT_BUFFER_MINUTES);
    const [minAgeToRent, setMinAgeToRent] = useState(DEFAULT_MIN_AGE);
    const [saving, setSaving] = useState(false);

    // Seed from whatever the company already has, so revisiting the step never silently
    // discards settings made in Settings → Preferences.
    useEffect(() => {
        if (!myCompanyData) return;

        setEnableOperationalHours(Boolean(myCompanyData.preferences?.enableOperationalHours));
        setOperationalHours(operationalHoursToPickerValue(myCompanyData.operationalHours));
        setTimezone(resolveCompanyTimeZone(myCompanyData));
        setBufferTimeMinutes(
            BUFFER_TIME_OPTIONS.includes(myCompanyData.bufferTimeMinutes)
                ? myCompanyData.bufferTimeMinutes
                : DEFAULT_BUFFER_MINUTES,
        );
        setMinAgeToRent(myCompanyData.minAgeToRent ?? DEFAULT_MIN_AGE);
    }, [myCompanyData]);

    const bufferOptions = useMemo(
        () =>
            BUFFER_TIME_OPTIONS.map((minutes) => ({
                value: minutes,
                label: minutes >= 60 ? t("steps.preferences.bufferHours", { count: minutes / 60 }) : `${minutes} min`,
            })),
        [t],
    );

    const submit = useCallback(async () => {
        setSaving(true);
        try {
            const hours = enableOperationalHours ? pickerValueToOperationalHours(operationalHours) : null;

            // Asking for hours but not receiving them would silently save the feature as "on"
            // with no window, which reads to the operator as the toggle not working.
            if (enableOperationalHours && !hours) {
                toast.error({ message: t("steps.preferences.hoursRequired") });
                return false;
            }

            // Same opening and closing minute leaves one bookable minute; the API rejects it.
            if (hours && hours.startMinutes === hours.endMinutes) {
                toast.error({ message: t("steps.preferences.hoursSameTime") });
                return false;
            }

            await apiClient.patch("/api/company/myCompany", {
                timezone: timezone || null,
                ...(hours && { operationalHours: hours }),
                bufferTimeMinutes,
                minAgeToRent,
                preferences: {
                    enableOperationalHours,
                },
            });

            await fetchCompanyData?.();
            await updateStepProgress("preferences", STEP_KEY_TO_INDEX.preferences ?? 6, true);
            return true;
        } catch (error) {
            const data = error?.response?.data;
            toast.error({
                message: t("messages.saveError"),
                description:
                    data?.errorSources?.map((source) => source?.message).filter(Boolean).join(" ") ||
                    (data?.message !== "Validation Error" ? data?.message : undefined),
            });
            return false;
        } finally {
            setSaving(false);
        }
    }, [
        enableOperationalHours,
        operationalHours,
        timezone,
        bufferTimeMinutes,
        minAgeToRent,
        fetchCompanyData,
        updateStepProgress,
        STEP_KEY_TO_INDEX.preferences, toast,
        t,
    ]);

    useImperativeHandle(ref, () => ({ submit }));

    return (
        <ConfigProvider theme={{ token: { colorPrimary: "#5d5dff" } }}>
            <div>
                <h1 className="text-[28px] font-bold tracking-tight text-gray-900 sm:text-[32px]">
                    {t("steps.preferences.heading")}
                </h1>
                <p className="mt-1 mb-8 text-sm leading-relaxed text-gray-500 sm:text-base">
                    {t("steps.preferences.description")}
                </p>

                <div className="flex flex-col gap-4">
                    {/* Operational hours */}
                    <div className="rounded-2xl border border-gray-200 bg-white px-6 py-5">
                        <div className="flex items-start justify-between gap-5">
                            <div>
                                <h5 className="mb-1 text-base font-semibold text-gray-900">
                                    {t("steps.preferences.operationalHours")}
                                </h5>
                                <p className="text-sm text-gray-500">
                                    {t("steps.preferences.operationalHoursDescription")}
                                </p>
                            </div>
                            <Switch
                                checked={enableOperationalHours}
                                onChange={setEnableOperationalHours}
                                disabled={saving}
                            />
                        </div>

                        {enableOperationalHours && (
                            <div className="mt-5 flex flex-col gap-4 border-t border-gray-100 pt-5">
                                <div className="flex flex-col gap-2">
                                    <span className="text-sm font-medium text-gray-700">
                                        {t("steps.preferences.hoursLabel")}
                                    </span>
                                    <RangePicker
                                        format="hh:mm A"
                                        minuteStep={30}
                                        value={operationalHours}
                                        onChange={setOperationalHours}
                                        disabled={saving}
                                        className="w-full max-w-sm rounded-full"
                                        getPopupContainer={() => document.body}
                                        changeOnScroll
                                    />
                                </div>

                                <div className="flex flex-col gap-2">
                                    <span className="text-sm font-medium text-gray-700">
                                        {t("steps.preferences.timezoneLabel")}
                                    </span>
                                    <Select
                                        value={timezone}
                                        onChange={setTimezone}
                                        options={buildTimeZoneSelectOptions(timezone, {
                                            us: t("steps.preferences.timezoneGroupUs"),
                                            other: t("steps.preferences.timezoneGroupOther"),
                                        })}
                                        disabled={saving}
                                        className="w-full max-w-sm"
                                        optionFilterProp="label"
                                        showSearch
                                    />
                                    <span className="text-xs text-gray-400">
                                        {t("steps.preferences.timezoneHint")}
                                    </span>
                                </div>

                                <p className="rounded-xl bg-[#f5f5ff] px-4 py-3 text-xs text-gray-600">
                                    {t("steps.preferences.locationOverrideNote")}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Buffer time */}
                    <div className="flex items-center justify-between gap-5 rounded-2xl border border-gray-200 bg-white px-6 py-5">
                        <div>
                            <h5 className="mb-1 text-base font-semibold text-gray-900">
                                {t("steps.preferences.bufferTime")}
                            </h5>
                            <p className="text-sm text-gray-500">{t("steps.preferences.bufferTimeDescription")}</p>
                        </div>
                        <Select
                            value={bufferTimeMinutes}
                            onChange={setBufferTimeMinutes}
                            options={bufferOptions}
                            disabled={saving}
                            className="w-40 shrink-0"
                        />
                    </div>

                    {/* Minimum age */}
                    <div className="flex items-center justify-between gap-5 rounded-2xl border border-gray-200 bg-white px-6 py-5">
                        <div>
                            <h5 className="mb-1 text-base font-semibold text-gray-900">
                                {t("steps.preferences.minimumAge")}
                            </h5>
                            <p className="text-sm text-gray-500">{t("steps.preferences.minimumAgeDescription")}</p>
                        </div>
                        <InputNumber
                            min={18}
                            max={99}
                            value={minAgeToRent}
                            onChange={(value) => setMinAgeToRent(value ?? DEFAULT_MIN_AGE)}
                            disabled={saving}
                            className="w-40 shrink-0"
                        />
                    </div>
                </div>
            </div>
        </ConfigProvider>
    );
});

Preferences.displayName = "Preferences";

export default Preferences;
